import { useEffect, useMemo } from 'react';
import { createHookContext } from '@clover-util/react-context';
import { useMerchant } from 'hooks/context/merchant';
import { useServiceType } from 'hooks/context/serviceType';
import { useGetMenuResource } from 'hooks/useApi';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { Category, Item, Menu } from 'olo-service';
import { useTranslation } from 'react-i18next';
import { isNotFoundError } from 'util/axiosHelpers';

export const GIFTCARD_CATEGORY_ID = 'giftcard-category';
export const GIFTCARD_MENU_ITEM_ID = 'giftcard-item';

export type MenuState = ReturnType<typeof useMenu>;

export const { MenuProvider, useMenu } = createHookContext('Menu', () => {
  const { isLoading: isMerchantLoading, merchant } = useMerchant();
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { currentServiceType } = useServiceType();

  const isOloEnabled = merchant?.onlineServices?.ONLINE_ORDERING?.enabled;

  // Currently, the menu returned by the API is only for online ordering
  //  If online ordering service isn't enabled, don't fetch it by forcing the id to be undefined
  const { loading, menu, error } = useGetMenuResource(isOloEnabled ? merchant?.merchantUuid : undefined, currentServiceType);

  const fullyLoaded = (!!menu || !!error) && !loading;

  const enhancedMenu = useMemo<Menu & { hasGiftCard?: boolean } | undefined>(() => {
    if (!merchant) return menu;

    const { onlineServices, name } = merchant;

    if (!onlineServices?.GIFT_CARDS?.enabled) return menu;

    const item: Item = {
      id: GIFTCARD_MENU_ITEM_ID,
      name: t('giftCards.digitalGiftCard'),
      price: 0,
      pricePlaceholder: t('giftCards.customValue'),
      description: t('giftCards.description', { merchantName: name }),
      images: [],
      modifierGroups: [],
      categoryIds: [GIFTCARD_CATEGORY_ID],
      available: true
    };

    const category: Category = {
      id: GIFTCARD_CATEGORY_ID,
      name: t('giftCards.categoryName'),
      sortOrder: Number.MAX_SAFE_INTEGER,
      items: [item],
    };

    return {
      ...menu,
      items: { ...menu?.items, [item.id]: item },
      categories: [...(menu?.categories ?? []), category],
      hasGiftCard: true,
    };
  }, [menu, merchant, t]);

  useEffect(() => {
    if (loading || (!menu && !error) || !isNotFoundError(error)) return;

    navigate('~/not-found');
  }, [error, loading, menu, navigate]);

  return {
    menu: enhancedMenu,
    loading: isMerchantLoading || !fullyLoaded,
    error,
  };
});
